import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useRef } from 'react';

import { useTimeIndicatorContext } from './useTimeIndicatorContext';

// A single-ping ("sparse") segment renders as a ~3px tick (see CycleSegment)
// sitting at the edge of the no-signal gap around it. Allow a slightly larger
// hover footprint so the cursor can land on that tick and read its time/speed
// instead of the hover being swallowed by the surrounding gap (TRE-15314).
const HOVER_EDGE_TOLERANCE_IN_PX = 4;

const useIndicatorValues = (
  minTime: string,
  maxTime: string,
  noSignalBlocks: Dayjs[][],
) => {
  const unixStart = dayjs(minTime).valueOf();
  const unixEnd = dayjs(maxTime).valueOf();

  const graphRef = useRef<HTMLDivElement>();
  const { indicatorValues, setIndicatorValues } = useTimeIndicatorContext();

  useEffect(() => {
    const updateIndicatorValues = (e: MouseEvent) => {
      const newIndicatorProps = {
        indicatorVisible: false,
        indicatorPosition: 0,
        indicatorTime: 0,
      };

      if (!graphRef.current) return;

      const graphRect = graphRef.current.getBoundingClientRect();
      const mouseX = e.clientX - graphRect.left;

      newIndicatorProps.indicatorPosition = mouseX;

      newIndicatorProps.indicatorTime =
        unixStart + (unixEnd - unixStart) * (mouseX / graphRect.width);

      const msPerPx = graphRect.width > 0 ? (unixEnd - unixStart) / graphRect.width : 0;
      const edgeToleranceMs = HOVER_EDGE_TOLERANCE_IN_PX * msPerPx;

      const inNoSignal = noSignalBlocks.some(([blockStart, blockEnd]) => {
        // Keep a tolerance-sized hoverable margin at each gap edge, where the
        // sparse-ping ticks sit, so they remain readable on hover.
        const hoverableStart = blockStart.valueOf() + edgeToleranceMs;
        const hoverableEnd = blockEnd.valueOf() - edgeToleranceMs;

        return (
          hoverableEnd > hoverableStart &&
          newIndicatorProps.indicatorTime > hoverableStart &&
          newIndicatorProps.indicatorTime < hoverableEnd
        );
      });

      if (!inNoSignal) {
        newIndicatorProps.indicatorVisible = true;
      }

      setIndicatorValues(newIndicatorProps);
    };

    const showIndicator = () =>
      setIndicatorValues({
        indicatorVisible: true,
        indicatorPosition: 0,
        indicatorTime: 0,
      });

    const hideIndicator = () =>
      setIndicatorValues({
        indicatorVisible: false,
        indicatorPosition: 0,
        indicatorTime: 0,
      });

    if (graphRef.current) {
      graphRef.current.addEventListener('mousemove', updateIndicatorValues);
      graphRef.current.addEventListener('mouseenter', showIndicator);
      graphRef.current.addEventListener('mouseleave', hideIndicator);
    }

    return () => {
      if (graphRef.current) {
        graphRef.current.removeEventListener('mousemove', updateIndicatorValues);
        graphRef.current.removeEventListener('mouseenter', showIndicator);
        graphRef.current.removeEventListener('mouseleave', hideIndicator);
      }
    };
  }, [setIndicatorValues, unixStart, unixEnd, noSignalBlocks]);

  return { graphRef, indicatorValues };
};

export { useIndicatorValues };
